import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom-v5-compat';
import {
  selectUuid,
  selectHydrated,
  selectVeteranContactEmail,
  selectInvitationData,
  selectFlowType,
  selectSessionInvalidated,
  hydrateFormData,
  clearFormData,
  loadFormDataFromStorage,
} from '../redux/slices/formSlice';
import { AUTH_LEVELS, FLOW_TYPES, URLS } from '../utils/constants';
import { getVassToken, isTokenExpired, removeVassToken } from '../utils/auth';
import {
  buildInvitationSearch,
  hasCompleteInvitation,
  parseInvitationFromSearch,
} from '../utils/invitationQuery';

/**
 * HOC that handles route authorization based on auth level.
 *
 * @param {React.Component} Component - The component to wrap
 * @param {'lowAuthOnly'|'token'} authLevel - The required authorization level for this route
 * @returns {React.Component} - The wrapped component with authorization logic
 */
const withAuthorization = (Component, authLevel = AUTH_LEVELS.TOKEN) => {
  return props => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const token = getVassToken();
    const sessionInvalidated = useSelector(selectSessionInvalidated);
    const flowType = useSelector(selectFlowType);
    const tokenExpired = token ? isTokenExpired(token) : false;
    const hasValidToken = token && !tokenExpired && !sessionInvalidated;
    const uuid = useSelector(selectUuid);
    const veteranContactEmail = useSelector(selectVeteranContactEmail);
    const invitationData = useSelector(selectInvitationData);
    const hydrated = useSelector(selectHydrated);
    const [isHydrating, setIsHydrating] = useState(!hydrated);

    const requiresToken = authLevel === AUTH_LEVELS.TOKEN;

    const invitationFromUrl = useMemo(
      () => parseInvitationFromSearch(location.search),
      [location.search],
    );

    // Attempt to hydrate from sessionStorage on mount
    useEffect(() => {
      if (!hydrated) {
        const savedData = loadFormDataFromStorage();
        if (savedData) {
          dispatch(hydrateFormData(savedData));
        }
        setIsHydrating(false);
      } else {
        setIsHydrating(false);
      }
    }, [hydrated, hasValidToken, dispatch]);

    // Handle token routes - redirect unauthenticated or expired token users to Verify page
    useEffect(() => {
      // Wait for hydration to complete before redirecting
      if (isHydrating) return;

      if (requiresToken && !hasValidToken) {
        // Remove stale token cookie if it exists (expired or session-invalidated)
        if (token && (tokenExpired || sessionInvalidated)) {
          removeVassToken();
        }

        // Clear form data when redirecting to Verify page
        dispatch(clearFormData());

        // Redirect to Verify: need invitation params so OTP can run; otherwise Verify shows error.
        const effectiveUuid = uuid ?? invitationFromUrl.uuid;
        const effectiveEmail =
          veteranContactEmail ?? invitationFromUrl.veteranContactEmail;
        const effectiveData = invitationData ?? invitationFromUrl.data;
        let redirectUrl = URLS.VERIFY;
        if (
          hasCompleteInvitation({
            uuid: effectiveUuid,
            veteranContactEmail: effectiveEmail,
            data: effectiveData,
          })
        ) {
          redirectUrl = `${URLS.VERIFY}${buildInvitationSearch({
            uuid: effectiveUuid,
            veteranContactEmail: effectiveEmail,
            data: effectiveData,
            cancel: flowType === FLOW_TYPES.CANCEL,
          })}`;
        }
        navigate(redirectUrl, {
          replace: true,
        });
      }
    }, [
      token,
      tokenExpired,
      hasValidToken,
      navigate,
      uuid,
      veteranContactEmail,
      invitationData,
      invitationFromUrl.uuid,
      invitationFromUrl.veteranContactEmail,
      invitationFromUrl.data,
      isHydrating,
      dispatch,
      requiresToken,
      sessionInvalidated,
      flowType,
    ]);

    // For token routes: don't render until hydration is complete and we have a valid token
    if (requiresToken && (isHydrating || !hasValidToken)) {
      return null;
    }

    return <Component {...props} />;
  };
};

export default withAuthorization;
